
import React, { useState, useMemo, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Link, } from 'react-router-dom';
import { Popover, Table } from 'antd';
import H3Text from '@Uilib/h3Text';
import { QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import RecentTransactions from '@Components/Inventory/ManageProducts/ViewProduct/RecentTransactions';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import HideValue from '../../../../Common/RestrictedAccess/HideValue';
import PRZModal from '../../../../Common/UI/PRZModal';
import './style.scss';
import { InfoCircleOutlined } from '@ant-design/icons';

const PurchaseRequestLines = ({
  poLines,
  visibleColumns,
  cfPurchaseRequestLine,
  selectedPurchaseRequest,
  fixedPriceEnabled
}) => {
  const [showPRZModal, setShowPRZModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const user = useSelector((state) => state.UserReducers.user);
  const MONEY = useSelector((state) => state.UserReducers.MONEY);
  const priceMasking = useSelector((state) => state.UserReducers.priceMasking);

  const { isDataMaskingPolicyEnable, isHideCostPrice } = priceMasking;

  const getPRColumns = useMemo(() => {
    const prLinesColumns = [
      {
        title: '',
        responsive: ['xs'],
        render: (record) => (
          <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
            <div className="mobile-list__item">
              <H3Text text={`SKU Number: ${record?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
              <H3Text text={record?.product_sku_name} className="mobile-list__item-info" />
              <H3Text text={`Quantity: ${QUANTITY(record?.quantity, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`} className="mobile-list__item-info" />
              {record.target_price && <H3Text text={`Target Price: ${MONEY(record.target_price)}`} className="mobile-list__item-info" />}
              {record.total_po_quantity && (
                <H3Text
                  text={`Purchased: ${record.total_po_quantity ? `${QUANTITY(record?.total_po_quantity || 0, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase()}` : '-'}`}
                  className="mobile-list__item-info"
                />
              )}
              {record?.st_gen_quantity && (
                <H3Text
                  text={`Transferred: ${record.st_gen_quantity ? `${QUANTITY(record?.st_gen_quantity || 0, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase()}` : '-'}`}
                  className="mobile-list__item-info"
                />
              )}
              <H3Text text={`Pending: ${QUANTITY(record?.quantity - ((record?.st_gen_quantity || 0) + (record?.total_po_quantity || 0)), record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`} className="mobile-list__item-info" />
              {!!fixedPriceEnabled && (
                <H3Text
                  text={`PRODUCT: ${QUANTITY(record?.quantity - ((record?.st_gen_quantity || 0) + (record?.total_po_quantity || 0)), record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`}
                  className="mobile-list__item-info"
                />
              )}
            </div>
          </Link>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Product',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <Fragment>
            <div onClick={() => {
              setShowPRZModal(true);
              setSelectedDocumentId(record?.product_sku_info?.product_sku_id);
            }}>
              <a>{`#${record?.product_sku_info?.internal_sku_code}`}</a>
            </div>
            <div className="flex-display" style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ maxWidth: '200px' }}>
                {`${record.product_sku_info?.ref_product_code ? `${record.product_sku_info?.ref_product_code} - ` : ''}${record.product_sku_name}`?.trim()}
                <div
                  dangerouslySetInnerHTML={{
                    __html: (isDataMaskingPolicyEnable && isHideCostPrice) ? '' : record?.remark,
                  }}
                  className="table-subscript"
                  style={{ width: '200px', whiteSpace: 'pre-wrap' }}
                />
                {(isDataMaskingPolicyEnable && isHideCostPrice) && <HideValue showPopOver />}
                {!record?.tenant_product_id && <H3Text text="New Product" className="view-pr-selection__new-product" />}
              </div>
              <div className="flex-align-center-justify-between" style={{ gap: '10px' }}>
                {record?.product_sku_info?.product_category_info?.category_path?.length > 0 && (
                  <ProductCategoryLabel
                    categoryPath={record?.product_sku_info?.product_category_info?.category_path}
                    categoryName={record?.product_sku_info?.product_category_info?.category_path?.at(-1)}
                    containerStyle={{ width: 'fit-content' }}
                  />
                )}
                {Helpers.getPermission(Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.CREATE, user) && (
                  <RecentTransactions
                    tenantId={selectedPurchaseRequest?.tenant_id}
                    productSkuId={record?.product_sku_info?.product_sku_id}
                    internalSkuCode={record?.product_sku_info?.internal_sku_code}
                    refProductCode={record?.product_sku_info?.ref_product_code}
                    productSkuName={record?.product_sku_name}
                  />
                )}
              </div>
            </div>
          </Fragment>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Quantity',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${QUANTITY(item?.quantity, item?.uom_info?.precision)} ${item?.uom_info?.uqc?.toProperCase()}`,
        visible: visibleColumns?.QUANTITY?.visible,
      },
      {
        title: 'Available Qty',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => {
          const uomInfo = record?.product_sku_info?.uom_info;
          const uomList = record?.product_sku_info?.uom_list;
          const currentDepartmentStock = record?.department_quantities?.find((item) => item?.tenant_department_id === selectedPurchaseRequest?.tenant_department_id) || 0;
          const filteredDepartmentsStock = record?.department_quantities?.filter((item) => item?.tenant_department_id !== selectedPurchaseRequest?.tenant_department_id);
          const totalStockInOtherDepartments = QUANTITY(filteredDepartmentsStock?.map((dept) => dept?.quantity).reduce((acc, cur) => acc + cur, 0) / record?.uom_info?.ratio, record?.uom_info?.precision);
          const totalPendingFromVendor = record?.pos_quantity_against_product?.map((quantity) => {
            const difference = quantity.total_pending_quantity - quantity.total_received_quantity;
            return difference;
          }).reduce((acc, cur) => acc + cur, 0);
          const quantityInDiffUoms = (
            <div>
              {
                uomList?.map((uom, i) => (
                  <div className="other-uom-stock" key={i}>
                    <div>
                      {QUANTITY(
                        (Number(currentDepartmentStock?.quantity ?? 0) * (Number(uomInfo?.ratio ?? 1)))
                        / (Number(uom?.ratio ?? 1)),
                        Number(uom?.precision ?? 0),
                      )}
                      {' '}

                    </div>
                    <div>
                      {uom?.uqc?.toProperCase()}
                    </div>
                  </div>
                ))
              }
            </div>
          );
          const hoverContentForPendingFromVendor = (
            <div>
              {record?.pos_quantity_against_product
                ?.filter((vendors) => vendors.total_pending_quantity - vendors.total_received_quantity > 0)
                ?.map((vendors) => (
                  <div
                    key={vendors.seller_id}
                    className="other-uom-stock"
                    onClick={() => {
                      window.open(Helpers.appendSystemDefaultFilterIdInURL(`/purchase/purchase-orders?page=1&limit=10&tenant=${selectedPurchaseRequest?.tenant_id}&status=ISSUED&keyword=${vendors?.seller_name}&po_receiving_status=PARTIALLY_RECEIVED,NOT_RECEIVED&product_search_from_pr=${record?.product_sku_info?.product_sku_id}&product_prefill=true`));
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <Link target="_blank" className="ellipsis">
                      {`${vendors?.internal_slr_code} - ${vendors?.seller_name?.toProperCase()}`}
                    </Link>
                    <H3Text
                      text={`${QUANTITY(vendors?.total_pending_quantity - vendors?.total_received_quantity) || 0} ${record?.uom_info?.uqc?.toProperCase()}`}
                      className="tenant-inventory-slider__dept-qty"
                    />
                  </div>
                ))}
            </div>
          );
          const hoverContentForStock = (
            <div>
              {filteredDepartmentsStock?.map((dept) => (
                <div key={dept.department_id} className="other-uom-stock">
                  <H3Text text={dept?.alias_name} />
                  <H3Text
                    text={`${QUANTITY(dept?.quantity * (record?.product_sku_info?.uom_info?.ratio || 1) / (record?.uom_info?.ratio || 1) || 0, record?.product_sku_info?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`}
                    className="tenant-inventory-slider__dept-qty"
                  />
                </div>
              ))}
            </div>
          );
          return (
            <Fragment>
              <Popover
                placement="bottom"
                content={quantityInDiffUoms}
                title="Quantity in other units"
                trigger="hover"
              >
                <div style={{ cursor: 'pointer' }}>
                  {`${QUANTITY(currentDepartmentStock?.quantity * (record?.product_sku_info?.uom_info?.ratio || 1) / (record?.uom_info?.ratio || 1) || 0, record?.product_sku_info?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}
                </div>
              </Popover>
              {totalStockInOtherDepartments > 0 && (
                <Popover
                  placement="bottom"
                  title="Stocks in other locations"
                  content={hoverContentForStock}
                  trigger="hover"
                >
                  <div
                    className="table-subscript"
                    style={{
                      color: 'rgb(109 107 106)',
                    }}
                  >
                    {`+${QUANTITY(totalStockInOtherDepartments, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase() || ''} in other locations`}
                    <InfoCircleOutlined style={{
                      color: 'rgb(45, 124, 246)',
                      margin: '2px',
                    }}
                    />
                  </div>
                </Popover>
              )}
              {totalPendingFromVendor > 0 && (
                <Popover
                  placement="bottom"
                  title="Pending from vendors"
                  content={hoverContentForPendingFromVendor}
                  trigger="hover"
                >
                  <div
                    className="table-subscript"
                    style={{
                      color: 'rgb(109 107 106)',
                    }}
                  >
                    {`+${QUANTITY(totalPendingFromVendor, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase() || ''} pending from vendors`}
                    <InfoCircleOutlined style={{
                      color: 'rgb(45, 124, 246)',
                      margin: '2px',
                    }}
                    />
                  </div>
                </Popover>
              )}
            </Fragment>
          );
        },
        visible: user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.stock_availability_visible,
      },
      {
        title: 'Target Price',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => ((isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view target price"} /> : MONEY(record.target_price)),
        visible: visibleColumns?.TARGET_PRICE?.visible,
      },
      {
        title: 'Purchased',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${item.total_po_quantity ? `${QUANTITY(item?.total_po_quantity || 0, item?.uom_info?.precision)}  ${item?.uom_info?.uqc?.toProperCase()}` : '-'}`,
        visible: visibleColumns?.PURCHASED?.visible,
      },
      {
        title: 'Transferred',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${item.st_gen_quantity ? `${QUANTITY(item?.st_gen_quantity || 0, item?.uom_info?.precision)}  ${item?.uom_info?.uqc?.toProperCase()}` : '-'}`,
        visible: visibleColumns?.TRANSFERRED?.visible,
      },
    ];

    if (cfPurchaseRequestLine?.length) {
      prLinesColumns.splice(4, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfPurchaseRequestLine, visibleColumns, null));
    }

    if (fixedPriceEnabled) {
      prLinesColumns.push({
        title: 'Pending',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${QUANTITY(item?.quantity - ((item?.st_gen_quantity || 0) + (item?.total_po_quantity || 0)), item?.uom_info?.precision)} ${item?.uom_info?.uqc?.toProperCase()}`,
        visible: !visibleColumns?.PENDING?.visible,
      });
      prLinesColumns.push({
        title: 'Total Value',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => ((isDataMaskingPolicyEnable && isHideCostPrice)
          ? <HideValue showPopOver popOverMessage="You don't have access to view total value" />
          : MONEY(Number(item.target_price) * Number(item.quantity))),
        fixed: 'right',
        visible: !visibleColumns?.TOTAL_VALUE?.visible,
      });
    }

    return prLinesColumns.filter(col => col.visible);
  }, [fixedPriceEnabled, visibleColumns, cfPurchaseRequestLine, user, priceMasking, MONEY, selectedPurchaseRequest]);

  return (
    <Fragment>
      <div className="pr-table-wrapper">
        <Table
          bordered
          showHeader
          title={() => `Material Request Items (${poLines?.length})`}
          size="small"
          columns={getPRColumns}
          dataSource={poLines || []}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          setShowPRZModal(false);
          setSelectedDocumentId(null);
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

export default PurchaseRequestLines;
